'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    Contract,
    deleteContract,
    getContracts,
} from '@/lib/api/administrative/contracts-api'
import { ApiResponsePagination } from '@/lib/api/request'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdDelete,
    MdDescription,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    DRAFT: { label: 'Rascunho', className: 'badge-ghost' },
    IN_ANALYSIS: { label: 'Em Análise', className: 'badge-warning' },
    SENT: { label: 'Enviado', className: 'badge-info' },
    APPROVED: { label: 'Aprovado', className: 'badge-success' },
    REJECTED: { label: 'Rejeitado', className: 'badge-error' },
    SIGNED: { label: 'Assinado', className: 'badge-secondary' },
}

const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'DRAFT', label: 'Rascunho' },
    { value: 'IN_ANALYSIS', label: 'Em Análise' },
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'SENT', label: 'Enviado' },
    { value: 'SIGNED', label: 'Assinado' },
    { value: 'REJECTED', label: 'Rejeitado' },
]

export default function ContractsListPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canCreate, canDelete } = useResourcePermissions(
        'administrative',
        'contracts'
    )

    const [contracts, setContracts] = useState<Contract[]>([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState<ApiResponsePagination | null>(
        null
    )
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [deleteModalId, setDeleteModalId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadContracts()
        }
    }, [user?.companyId, page, statusFilter])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (user?.companyId) {
                setPage(1)
                loadContracts()
            }
        }, 400)
        return () => clearTimeout(timeout)
    }, [searchTerm])

    const loadContracts = async () => {
        if (!user?.companyId) return
        setLoading(true)
        try {
            const response = await getContracts(user.companyId, {
                page,
                limit: 15,
                status: statusFilter || undefined,
                search: searchTerm || undefined,
            })
            if (response.success) {
                setContracts(response.result || [])
                if (response.pagination) {
                    setPagination(response.pagination)
                }
                if (response.total !== undefined) {
                    setTotal(response.total)
                }
            } else {
                toast.error(response.error || 'Erro ao carregar contratos')
            }
        } catch {
            toast.error('Erro ao carregar contratos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModalId) return
        setDeleting(true)
        try {
            const response = await deleteContract(deleteModalId)
            if (response.success) {
                toast.success('Contrato removido com sucesso')
                loadContracts()
            } else {
                toast.error(response.error || 'Erro ao remover contrato')
            }
        } catch {
            toast.error('Erro ao remover contrato')
        } finally {
            setDeleting(false)
            setDeleteModalId(null)
        }
    }

    if (loading && contracts.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando contratos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdDescription className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Contratos
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {total}{' '}
                            {total === 1
                                ? 'contrato cadastrado'
                                : 'contratos cadastrados'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        onClick={() =>
                            navigate('/administrative/contracts/new')
                        }
                        className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                    >
                        <MdAdd className="text-lg" />
                        Novo Contrato
                    </button>
                )}
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 max-w-md">
                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg z-10" />
                    <input
                        type="text"
                        placeholder="Buscar contratos..."
                        className="input input-bordered w-full pl-10"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <select
                    className="select select-bordered w-full sm:w-48"
                    value={statusFilter}
                    onChange={e => {
                        setStatusFilter(e.target.value)
                        setPage(1)
                    }}
                >
                    {STATUS_FILTER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Lista */}
            {contracts.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdDescription className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        {statusFilter || searchTerm
                            ? 'Nenhum contrato encontrado'
                            : 'Nenhum contrato cadastrado'}
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        {statusFilter || searchTerm
                            ? 'Tente ajustar os filtros de busca'
                            : 'Crie seu primeiro contrato para começar'}
                    </p>
                    {canCreate && !statusFilter && !searchTerm && (
                        <button
                            onClick={() =>
                                navigate('/administrative/contracts/new')
                            }
                            className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                        >
                            <MdAdd className="text-xl" />
                            Criar Primeiro Contrato
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto bg-base-100 rounded-2xl border border-base-300/50 shadow-sm">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Status</th>
                                    <th>Cláusulas</th>
                                    <th>Criado por</th>
                                    <th>Data</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {contracts.map(contract => {
                                    const statusInfo = STATUS_LABELS[
                                        contract.status
                                    ] || {
                                        label: contract.status,
                                        className: 'badge-ghost',
                                    }
                                    return (
                                        <tr
                                            key={contract.id}
                                            className="hover cursor-pointer"
                                            onClick={() =>
                                                navigate(
                                                    `/administrative/contracts/${contract.id}`
                                                )
                                            }
                                        >
                                            <td className="font-mono text-sm">
                                                #{contract.id}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${statusInfo.className}`}
                                                >
                                                    {statusInfo.label}
                                                </span>
                                            </td>
                                            <td>
                                                {contract.terms?.length || 0}
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-outline">
                                                    {contract.createdByType ===
                                                    'PARTNER'
                                                        ? contract.partner
                                                              ?.tradingName ||
                                                          contract.partner
                                                              ?.name ||
                                                          'Parceiro'
                                                        : 'Empresa'}
                                                </span>
                                            </td>
                                            <td className="text-sm text-base-content/60">
                                                {format(
                                                    new Date(
                                                        contract.createdAt
                                                    ),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </td>
                                            <td>
                                                {canDelete && (
                                                    <button
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            setDeleteModalId(
                                                                contract.id
                                                            )
                                                        }}
                                                        title="Remover"
                                                    >
                                                        <MdDelete className="text-base" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginação */}
                    {pagination && (pagination.pages?.length || 0) > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={pagination.first}
                                onClick={() => setPage(pagination.prev || 1)}
                            >
                                <MdArrowBack className="text-base" />
                            </button>
                            {pagination.pages?.map(p => (
                                <button
                                    key={p}
                                    className={`btn btn-sm ${
                                        p === pagination.page
                                            ? 'btn-secondary'
                                            : 'btn-ghost'
                                    }`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={pagination.end}
                                onClick={() => setPage(pagination.next || page)}
                            >
                                <MdArrowForward className="text-base" />
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConfirmModal
                isOpen={!!deleteModalId}
                onClose={() => setDeleteModalId(null)}
                onConfirm={handleDelete}
                title="Confirmar Exclusão"
                message="Tem certeza que deseja remover este contrato? Todas as cláusulas serão removidas."
                confirmButtonText="Remover"
                loading={deleting}
            />
        </div>
    )
}
